import { useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import LynIcon from 'components/@extended/LynIcon';
import TabPanel from 'components/TabPanel/TabPanel';
import plusIcon from 'assets/images/icons/plus.svg';
import comptroller from 'assets/images/comptroller_white.svg';
import { a11yProps, existTabItems } from 'pages/globals/global';
import SolutionCatalogIcon from 'assets/icons/solutions-catalog';
import ComptrollerPageHeader from 'components/header/ComptrollerPageHeader';
import ListViewLeaseManagementArea from './ListPageUtils/ListViewLeaseManagementArea';
import { Box, Button, Grid, useMediaQuery } from '@mui/material';
import { useGetAllIdentityListQuery } from 'store/features/process/processApi';

const LeaseManagement = ({ from = 'identity', forceActiveTab }) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const params = useParams();
	const [searchParams] = useSearchParams();
	// const [getAllIdentityCase, { data: getAllIdentityCaseData, isSuccess, isLoading, isUninitialized }] = useGetAllIdentityCaseMutation();

	const {
		data: getAllIdentityCaseData,
		isLoading,
		isUninitialized,
		refetch,
	} = useGetAllIdentityListQuery(
		{
			companyCode: '24155',
			operation: 'get-bulk',
			processCode: '10101',
			suffixCode: '',
		},
		{
			refetchOnMountOrArgChange: true,
			keepUnusedDataFor: 0,
		}
	);
	const [tabsLoaded, setTabsLoaded] = useState(false);

	// Updated TabsItem with new tabs
	const { profileData } = useSelector((state) => state?.profile);

	// Set isRowClick based on the same logic as IdentityCaseList (default to false for IdentityFeaturedTable)

	// Filter tabs to only show those with data
	const getTabsWithData = () => {
		// if (!getAllIdentityCaseData || getAllIdentityCaseData.length === 0) {
		// 	return [];
		// }

		return existTabItems.filter((tab) => {
			if (tab.data === 'All') {
				return true;
			}
			if (tab.data === 'Worklist') {
				return getAllIdentityCaseData?.some(
					(item) => item?.activationStatus === 'W-I-P' && item?.recordStatus === 'parked' && profileData?.userId === item?.approverUserId[0]
				);
			}
			if (tab.data === 'W-I-P') {
				// For W-I-P tab, exclude items that are in worklist (parked + user is approver)
				return getAllIdentityCaseData?.some(
					(item) =>
						item?.activationStatus === 'W-I-P' && !(item?.recordStatus === 'parked' && profileData?.userId === item?.approverUserId[0])
				);
			}
			if (tab.data === 'Draft') {
				return getAllIdentityCaseData?.some((item) => item?.activationStatus === 'draft');
			}
			// Check if there's at least one item with this activation status
			return getAllIdentityCaseData?.some((item) => item?.activationStatus === tab.data);
		});
	};

	// const TabsItem = getTabsWithData();
	const TabsItem = useMemo(() => getTabsWithData(), [getAllIdentityCaseData, profileData]);

	const [selectedTab, setSelectedTab] = useState(() => {
		const activeTab = searchParams.get('tab');

		if (activeTab) {
			const index = TabsItem?.findIndex((item) => item?.label?.toLowerCase()?.replaceAll('-', '') === activeTab);

			if (index > -1) {
				return index;
			}
		} else if (!forceActiveTab) {
			const index = TabsItem?.findIndex((item) =>
				(item?.label?.toLowerCase()?.replaceAll('-', '') === TabsItem?.[0]?.data) === 'Active' ? 'Active' : 'All'
			);

			if (index > -1) return index;
		} else if (forceActiveTab) {
			const index = TabsItem?.findIndex((item) => item?.label?.toLowerCase()?.replaceAll('-', '') === forceActiveTab?.toLowerCase());

			if (index > -1) return index;
		}
	});

	// const [isLoading, setActiveCreateButton] = useState(false);

	// const [getModuleList] = useGetModuleListMutation();

	// const [GetCompanyInfo, { isLoading }] = useGetCompanyInfoMutation();

	const matchDownSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));

	const selectTabHandler = (event, newValue) => {
		setSelectedTab(newValue);

		// Update URL without navigation
		// const newSearchParams = new URLSearchParams(window.location.search);
		// newSearchParams.set('tab', newValue);
		// newSearchParams.set('page', '0');
		// window.history.replaceState(null, '', `${window.location.pathname}?${newSearchParams.toString()}`);

		const tabName = TabsItem?.[newValue]?.label?.toLowerCase()?.replaceAll('-', '');

		if (params?.id) {
			navigate(`/static/lease-record-management-v6/${params.id}?tab=${tabName}`, { replace: true });
		} else {
			navigate(-1);
		}
	};

	// Filter data based on selected tab
	const getFilteredData = useMemo(() => {
		if (!getAllIdentityCaseData) return [];

		const tabData = TabsItem?.[selectedTab]?.data;

		if (tabData === 'All') {
			return getAllIdentityCaseData;
		}
		if (tabData === 'Worklist') {
			const data = getAllIdentityCaseData.filter(
				(item) => item?.activationStatus === 'W-I-P' && item?.recordStatus === 'parked' && profileData?.userId === item?.approverUserId[0]
			);
			return data;
		}
		if (tabData === 'W-I-P') {
			return getAllIdentityCaseData.filter(
				(item) => item?.activationStatus === 'W-I-P' && !(item?.recordStatus === 'parked' && profileData?.userId === item?.approverUserId[0])
			);
		}
		if (tabData === 'Draft') {
			return getAllIdentityCaseData.filter((item) => item?.activationStatus === 'draft');
		}

		return getAllIdentityCaseData.filter((item) => item?.activationStatus === tabData);
	}, [getAllIdentityCaseData, selectedTab, profileData, TabsItem]);

	// Create processDetails object for consistency
	const processDetails = {
		processCode: '10101',
		name: 'Lease Record Management',
		version: '1.0',
	};

	// Create permissions object - disable delete for lease management
	const permissionsObject = {
		read: true,
		write: true,
		edit: true,
		delete: false,
	};

	// Create headMetaData object
	const headMetaData = {
		meta: {
			staticFormUrl: '/static/lease-record-management-v6',
		},
	};

	// Function to refetch data after operations
	// const refetchData = () => {
	// getAllIdentityCase({
	// 	companyCode: '24155',
	// 	operation: 'get-bulk',
	// 	processCode: '10101',
	// 	suffixCode: '',
	// });
	// };

	useEffect(() => {
		// Only set the selected tab once we have data and tabs
		if (TabsItem.length > 0) {
			const activeTab = searchParams.get('tab');

			if (TabsItem?.length > 0 && activeTab) {
				const index = TabsItem?.findIndex((item) => item?.label?.toLowerCase()?.replaceAll('-', '') === activeTab);

				if (index > -1) {
					setSelectedTab(index);

					setTabsLoaded(true);
					return;
				}
			}

			if (!forceActiveTab) {
				const index = TabsItem?.findIndex((item) =>
					(item?.label?.toLowerCase()?.replaceAll('-', '') === TabsItem?.[0]?.data) === 'Active' ? 'Active' : 'All'
				);

				setSelectedTab(index);
			}

			setTabsLoaded(true);
		}
	}, [TabsItem, searchParams]);

	// useEffect(() => {
	// 	setActiveCreateButton(isLoading || isDataLoading);
	// }, [isLoading, isDataLoading]);

	// useEffect(() => {
	// 	if (profileData) {
	// 		GetCompanyInfo({
	// 			companyCode: profileData?.creatorOrganizationId,
	// 		});
	// 	}
	// }, [GetCompanyInfo, profileData]);

	// useEffect(() => {
	// 	getAllIdentityCase({
	// 		companyCode: '24155',
	// 		operation: 'get-bulk',
	// 		processCode: '10101',
	// 		suffixCode: '',
	// 	});
	// }, [refresh]);

	// Update data state when getAllIdentityCaseData changes
	// useEffect(() => {
	// 	if (getAllIdentityCaseData) {
	// 		setData(getAllIdentityCaseData);
	// 	}
	// }, [getAllIdentityCaseData]);

	return (
		<Grid container rowSpacing={4.5} columnSpacing={2.75}>
			<Grid item xs={12}>
				<Grid item xs={12}>
					{from === 'identity' && (
						// <Box
						// 	sx={{
						// 		width: '100%',
						// 		borderRadius: 1,
						// 	}}
						// >
						// 	<Stack
						// 		direction="row"
						// 		alignItems="center"
						// 		justifyContent="space-between"
						// 		sx={{
						// 			px: '15px',
						// 			py: '12px',
						// 			color: 'common.white',
						// 			bgcolor: 'header.background.primary',
						// 		}}
						// 		style={{ borderRadius: '6px 6px 0 0' }}
						// 	>
						// 		<Stack direction="row" alignItems="center" sx={{ gap: '10px' }}>
						// 			<img alt="comptroller" src={comptroller} width="20px" height="18px" />

						// 			<Typography variant="h4">Lease Record Management</Typography>
						// 		</Stack>

						// 		<Button
						// 			sx={{
						// 				backgroundColor: isLoading ? 'gray' : theme.palette.lynTheme.success,
						// 				boxShadow: 'none',
						// 				border: `1px solid ${isLoading ? 'gray' : theme.palette.lynTheme.success}`,
						// 				borderColor: '#ffff',
						// 				padding: '3px 15px',
						// 				display: 'flex',
						// 				alignItems: 'center',
						// 				color: 'white',
						// 				transition: 'all 0.3s',
						// 				fontSize: '13px',
						// 				fontWeight: '500',
						// 				borderRadius: '6px',
						// 				'&:hover': {
						// 					backgroundColor: isLoading ? 'gray' : '#207f3b',
						// 					transform: 'scale(0.9)',
						// 				},
						// 			}}
						// 			type="primary"
						// 			disabled={isLoading}
						// 			onClick={() =>
						// 				navigate('/static/lease-record-management-v6/create', {
						// 					state: {
						// 						leaseList: getAllIdentityCaseData,
						// 						processCode: '10101',
						// 					},
						// 				})
						// 			}
						// 		>
						// 			<img src={plusIcon} alt="add" width="12" height="12" style={{ marginRight: '8px' }} />
						// 			Create
						// 		</Button>
						// 	</Stack>

						// 	{tabsLoaded && (
						// 		<Box
						// 			sx={{
						// 				width: '100%',
						// 				backgroundColor: 'white',
						// 				'& .MuiTab-root': {
						// 					backgroundColor: 'transparent',
						// 				},
						// 			}}
						// 			style={{ borderRadius: '0px 0px 6px 6px' }}
						// 		>
						// 			<Tabs
						// 				value={selectedTab}
						// 				sx={{
						// 					'& .MuiTabs-indicator': {
						// 						backgroundColor: '#214689',
						// 						borderRadius: '0px 0px 0px 6px',
						// 					},
						// 					//active tab color
						// 					'& .Mui-selected': {
						// 						color: '#214689',
						// 					},

						// 					'& .MuiTab-root': {
						// 						backgroundColor: 'white',
						// 						opacity: 1,
						// 					},
						// 				}}
						// 				variant={matchDownSM ? 'scrollable' : 'standard'}
						// 				scrollButtons
						// 				allowScrollButtonsMobile
						// 				textColor="inherit"
						// 				onChange={selectTabHandler}
						// 			>
						// 				{TabsItem?.map((item, index) => (
						// 					<Tab
						// 						key={index}
						// 						sx={{
						// 							padding: '5px 50px',
						// 						}}
						// 						icon={<LynIcon folder="icons" fileName={item.icon} format="svg" sx={{ marginRight: '16px' }} />}
						// 						iconPosition="start"
						// 						label={item.label}
						// 						value={item.data}
						// 						{...a11yProps(index)}
						// 					/>
						// 				))}
						// 			</Tabs>
						// 		</Box>
						// 	)}
						// </Box>

						<ComptrollerPageHeader
							tabsItem={TabsItem}
							selectedTab={selectedTab}
							title="Lease Record Management"
							selectTabHandler={selectTabHandler}
							image={<SolutionCatalogIcon color="#fff" width="20px" height="20px" />}
							navigate={() =>
								navigate('/static/lease-record-management-v6/create', {
									state: {
										leaseList: getAllIdentityCaseData,
										processCode: '10101',
									},
								})
							}
						/>
					)}

					<Box sx={{ mt: 2 }}>
						{tabsLoaded && (
							<Box sx={{ borderBottom: 1, borderColor: 'divider', width: '100%' }}>
								{TabsItem?.map((tab, index) => (
									<TabPanel key={index} value={selectedTab} index={index} sx={{ height: 'calc(100% - 38px)' }}>
										{/* <TabPanel key={index} value={selectedTab} index={tab.data} sx={{ height: 'calc(100% - 38px)' }}> */}
										<Box sx={{ background: '#fff', padding: '20px', borderRadius: '4px' }}>
											<ListViewLeaseManagementArea
												currentRoute="v6"
												refetchData={refetch}
												leaseList={getFilteredData}
												headMetaData={headMetaData}
												// selectedTabName={selectedTab}
												processDetails={processDetails}
												permissionsObject={permissionsObject}
												isLoading={isLoading || isUninitialized}
												selectedTabName={TabsItem?.[selectedTab]?.data}
												expectedURLToRedirect="/static/lease-record-management-v6"
											/>
										</Box>
									</TabPanel>
								))}
							</Box>
						)}
					</Box>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default LeaseManagement;

// import { useSelector } from 'react-redux';
// import { useEffect, useMemo, useState } from 'react';
// import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
// import { useTheme } from '@mui/material/styles';
// import LynIcon from 'components/@extended/LynIcon';
// import TabPanel from 'components/TabPanel/TabPanel';
// import plusIcon from 'assets/images/icons/plus.svg';
// import comptroller from 'assets/images/comptroller_white.svg';
// import { a11yProps, existTabItems } from 'pages/globals/global';
// // import ListViewKeyManagementArea from './ListPageUtils/ListViewKeyManagementArea';
// import { Box, Button, Grid, Stack, Tab, Tabs, Typography, useMediaQuery } from '@mui/material';
// import { useGetAllIdentityCaseMutation, useGetAllIdentityListQuery } from 'store/features/process/processApi';
// import ListViewLeaseManagementArea from './ListPageUtils/ListViewLeaseManagementArea';

// const LeaseManagement = ({ from = 'identity', forceActiveTab }) => {
// 	const theme = useTheme();
// 	const navigate = useNavigate();
// 	const params = useParams();
// 	const [searchParams] = useSearchParams();
// 	// const [getAllIdentityCase, { data: getAllIdentityCaseData, isSuccess, isLoading, isUninitialized }] = useGetAllIdentityCaseMutation();

// 	const {
// 		data: getAllIdentityCaseData,
// 		isLoading,
// 		isUninitialized,
// 		refetch,
// 	} = useGetAllIdentityListQuery(
// 		{
// 			companyCode: '24155',
// 			operation: 'get-bulk',
// 			processCode: '10101',
// 			suffixCode: '',
// 		},
// 		{
// 			refetchOnMountOrArgChange: true,
// 			keepUnusedDataFor: 0,
// 		}
// 	);
// 	const [tabsLoaded, setTabsLoaded] = useState(false);

// 	// Updated TabsItem with new tabs
// 	const { profileData } = useSelector((state) => state?.profile);

// 	// Set isRowClick based on the same logic as IdentityCaseList (default to false for IdentityFeaturedTable)

// 	// Filter tabs to only show those with data
// 	const getTabsWithData = () => {
// 		// if (!getAllIdentityCaseData || getAllIdentityCaseData.length === 0) {
// 		// 	return [];
// 		// }

// 		return existTabItems.filter((tab) => {
// 			if (tab.data === 'All') {
// 				return true;
// 			}
// 			if (tab.data === 'Worklist') {
// 				return getAllIdentityCaseData?.some(
// 					(item) => item?.activationStatus === 'W-I-P' && item?.recordStatus === 'parked' && profileData?.userId === item?.approverUserId[0]
// 				);
// 			}
// 			if (tab.data === 'W-I-P') {
// 				// For W-I-P tab, exclude items that are in worklist (parked + user is approver)
// 				return getAllIdentityCaseData?.some(
// 					(item) =>
// 						item?.activationStatus === 'W-I-P' && !(item?.recordStatus === 'parked' && profileData?.userId === item?.approverUserId[0])
// 				);
// 			}
// 			if (tab.data === 'Draft') {
// 				return getAllIdentityCaseData?.some((item) => item?.activationStatus === 'draft');
// 			}
// 			// Check if there's at least one item with this activation status
// 			return getAllIdentityCaseData?.some((item) => item?.activationStatus === tab.data);
// 		});
// 	};

// 	// const TabsItem = getTabsWithData();
// 	const TabsItem = useMemo(() => getTabsWithData(), [getAllIdentityCaseData, profileData]);

// 	const [selectedTab, setSelectedTab] = useState(() => {
// 		const activeTab = searchParams.get('tab');

// 		if (activeTab) {
// 			const foundTab = TabsItem.find((item) => item.data === activeTab);
// 			if (foundTab?.data) {
// 				return foundTab?.data;
// 			}
// 		} else if (!forceActiveTab) {
// 			return TabsItem?.[0]?.data;
// 		} else if (forceActiveTab) {
// 			return forceActiveTab;
// 		}
// 	});

// 	// const [isLoading, setActiveCreateButton] = useState(false);

// 	// const [getModuleList] = useGetModuleListMutation();

// 	// const [GetCompanyInfo, { isLoading }] = useGetCompanyInfoMutation();

// 	const matchDownSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));

// 	const selectTabHandler = (event, newValue) => {
// 		setSelectedTab(newValue);

// 		// Update URL without navigation
// 		// const newSearchParams = new URLSearchParams(window.location.search);
// 		// newSearchParams.set('tab', newValue);
// 		// newSearchParams.set('page', '0');
// 		// window.history.replaceState(null, '', `${window.location.pathname}?${newSearchParams.toString()}`);

// 		if (params?.id) {
// 			navigate(`/static/lease-record-management-v6/${params.id}?tab=${newValue}`, { replace: true });
// 		} else {
// 			navigate(-1);
// 		}
// 	};

// 	// Filter data based on selected tab
// 	const getFilteredData = useMemo(() => {
// 		if (!getAllIdentityCaseData) return [];

// 		if (selectedTab === 'All') {
// 			return getAllIdentityCaseData;
// 		}
// 		if (selectedTab === 'Worklist') {
// 			const data = getAllIdentityCaseData.filter(
// 				(item) => item?.activationStatus === 'W-I-P' && item?.recordStatus === 'parked' && profileData?.userId === item?.approverUserId[0]
// 			);
// 			return data;
// 		}
// 		if (selectedTab === 'W-I-P') {
// 			return getAllIdentityCaseData.filter(
// 				(item) => item?.activationStatus === 'W-I-P' && !(item?.recordStatus === 'parked' && profileData?.userId === item?.approverUserId[0])
// 			);
// 		}
// 		if (selectedTab === 'Draft') {
// 			return getAllIdentityCaseData.filter((item) => item?.activationStatus === 'draft');
// 		}

// 		return getAllIdentityCaseData.filter((item) => item?.activationStatus === selectedTab);
// 	}, [getAllIdentityCaseData, selectedTab, profileData, TabsItem]);

// 	// Create processDetails object for consistency
// 	const processDetails = {
// 		processCode: '10101',
// 		name: 'Lease Record Management',
// 		version: '1.0',
// 	};

// 	// Create permissions object - disable delete for lease management
// 	const permissionsObject = {
// 		read: true,
// 		write: true,
// 		edit: true,
// 		delete: false,
// 	};

// 	// Create headMetaData object
// 	const headMetaData = {
// 		meta: {
// 			staticFormUrl: '/static/lease-record-management-v6',
// 		},
// 	};

// 	// Function to refetch data after operations
// 	// const refetchData = () => {
// 	// getAllIdentityCase({
// 	// 	companyCode: '24155',
// 	// 	operation: 'get-bulk',
// 	// 	processCode: '10101',
// 	// 	suffixCode: '',
// 	// });
// 	// };

// 	useEffect(() => {
// 		// Only set the selected tab once we have data and tabs
// 		if (TabsItem.length > 0) {
// 			const activeTab = searchParams.get('tab');

// 			if (activeTab) {
// 				const foundTab = TabsItem.find((item) => item.data === activeTab);
// 				if (foundTab?.data) {
// 					setSelectedTab(foundTab?.data);
// 					setTabsLoaded(true);
// 					return;
// 				}
// 			}

// 			if (!forceActiveTab) {
// 				setSelectedTab(TabsItem?.[0]?.data === 'Active' ? 'Active' : 'All');
// 			}

// 			setTabsLoaded(true);
// 		}
// 	}, [TabsItem, searchParams]);

// 	// useEffect(() => {
// 	// 	setActiveCreateButton(isLoading || isDataLoading);
// 	// }, [isLoading, isDataLoading]);

// 	// useEffect(() => {
// 	// 	if (profileData) {
// 	// 		GetCompanyInfo({
// 	// 			companyCode: profileData?.creatorOrganizationId,
// 	// 		});
// 	// 	}
// 	// }, [GetCompanyInfo, profileData]);

// 	// useEffect(() => {
// 	// 	getAllIdentityCase({
// 	// 		companyCode: '24155',
// 	// 		operation: 'get-bulk',
// 	// 		processCode: '10101',
// 	// 		suffixCode: '',
// 	// 	});
// 	// }, [refresh]);

// 	// Update data state when getAllIdentityCaseData changes
// 	// useEffect(() => {
// 	// 	if (getAllIdentityCaseData) {
// 	// 		setData(getAllIdentityCaseData);
// 	// 	}
// 	// }, [getAllIdentityCaseData]);

// 	return (
// 		<Grid container rowSpacing={4.5} columnSpacing={2.75}>
// 			<Grid item xs={12}>
// 				<Grid item xs={12}>
// 					{from === 'identity' && (
// 						<Box
// 							sx={{
// 								width: '100%',
// 								borderRadius: 1,
// 							}}
// 						>
// 							<Stack
// 								direction="row"
// 								alignItems="center"
// 								justifyContent="space-between"
// 								sx={{
// 									px: '15px',
// 									py: '12px',
// 									color: 'common.white',
// 									bgcolor: 'header.background.primary',
// 								}}
// 								style={{ borderRadius: '6px 6px 0 0' }}
// 							>
// 								<Stack direction="row" alignItems="center" sx={{ gap: '10px' }}>
// 									<img alt="comptroller" src={comptroller} width="20px" height="18px" />

// 									<Typography variant="h4">Lease Record Management</Typography>
// 								</Stack>

// 								<Button
// 									sx={{
// 										backgroundColor: isLoading ? 'gray' : theme.palette.lynTheme.success,
// 										boxShadow: 'none',
// 										border: `1px solid ${isLoading ? 'gray' : theme.palette.lynTheme.success}`,
// 										borderColor: '#ffff',
// 										padding: '3px 15px',
// 										display: 'flex',
// 										alignItems: 'center',
// 										color: 'white',
// 										transition: 'all 0.3s',
// 										fontSize: '13px',
// 										fontWeight: '500',
// 										borderRadius: '6px',
// 										'&:hover': {
// 											backgroundColor: isLoading ? 'gray' : '#207f3b',
// 											transform: 'scale(0.9)',
// 										},
// 									}}
// 									type="primary"
// 									disabled={isLoading}
// 									onClick={() =>
// 										navigate('/static/lease-record-management-v6/create', {
// 											state: {
// 												leaseList: getAllIdentityCaseData,
// 												processCode: '10101',
// 											},
// 										})
// 									}
// 								>
// 									<img src={plusIcon} alt="add" width="12" height="12" style={{ marginRight: '8px' }} />
// 									Create
// 								</Button>
// 							</Stack>

// 							{tabsLoaded && (
// 								<Box
// 									sx={{
// 										width: '100%',
// 										backgroundColor: 'white',
// 										'& .MuiTab-root': {
// 											backgroundColor: 'transparent',
// 										},
// 									}}
// 									style={{ borderRadius: '0px 0px 6px 6px' }}
// 								>
// 									<Tabs
// 										value={selectedTab}
// 										sx={{
// 											'& .MuiTabs-indicator': {
// 												backgroundColor: '#214689',
// 												borderRadius: '0px 0px 0px 6px',
// 											},
// 											//active tab color
// 											'& .Mui-selected': {
// 												color: '#214689',
// 											},

// 											'& .MuiTab-root': {
// 												backgroundColor: 'white',
// 												opacity: 1,
// 											},
// 										}}
// 										variant={matchDownSM ? 'scrollable' : 'standard'}
// 										scrollButtons
// 										allowScrollButtonsMobile
// 										textColor="inherit"
// 										onChange={selectTabHandler}
// 									>
// 										{TabsItem?.map((item, index) => (
// 											<Tab
// 												key={index}
// 												sx={{
// 													padding: '5px 50px',
// 												}}
// 												icon={<LynIcon folder="icons" fileName={item.icon} format="svg" sx={{ marginRight: '16px' }} />}
// 												iconPosition="start"
// 												label={item.label}
// 												value={item.data}
// 												{...a11yProps(index)}
// 											/>
// 										))}
// 									</Tabs>
// 								</Box>
// 							)}
// 						</Box>
// 					)}

// 					<Box sx={{ mt: 2 }}>
// 						{tabsLoaded && (
// 							<Box sx={{ borderBottom: 1, borderColor: 'divider', width: '100%' }}>
// 								{TabsItem?.map((tab, index) => (
// 									<TabPanel key={index} value={selectedTab} index={tab.data} sx={{ height: 'calc(100% - 38px)' }}>
// 										<Box sx={{ background: '#fff', padding: '20px', borderRadius: '4px' }}>
// 											<ListViewLeaseManagementArea
// 												isLoading={isLoading || isUninitialized}
// 												leaseList={getFilteredData}
// 												selectedTabName={selectedTab}
// 												currentRoute="v6"
// 												// isRowClick={isRowClick}
// 												// setData={setData}
// 												processDetails={processDetails}
// 												permissionsObject={permissionsObject}
// 												headMetaData={headMetaData}
// 												expectedURLToRedirect="/static/lease-record-management-v6"
// 												refetchData={refetch}
// 											/>
// 										</Box>
// 									</TabPanel>
// 								))}
// 							</Box>
// 						)}
// 					</Box>
// 				</Grid>
// 			</Grid>
// 		</Grid>
// 	);
// };

// export default LeaseManagement;
